import styled from '@emotion/styled';
import { companyData, countryAndRegions } from '@gameonly/core';
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import LanguageSwitcher from '../layout/LanguageSwitcher';
import BaseBlock from '../ui/Block';
import LoadingButton from '../ui/LoadingButton';
import Markdown from '../ui/Markdown';
import { useAuthentication } from './context/useAuthentication';

const Container = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 0;
  @media (max-height: 750px) {
    height: 100%;
  }
  @media (max-width: 700px) {
    height: 100%;
    padding: 0;
  }
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const Block = styled(BaseBlock)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 32px 38px;
  max-width: 850px;
  margin-top: auto;
  margin-bottom: auto;
`;

const TwoColumnContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
  @media (max-width: 700px) {
    flex-direction: column;
    gap: 8px;
  }
  > * {
    flex: 1;
  }
`;

const ErrorBlock = styled(BaseBlock)`
  width: 100%;
`;

const SideActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  font-size: 0.8rem;
  margin-top: 16px;
  align-items: center;
`;

const TosContainer = styled.div`
  margin-bottom: 8px;
  a {
    font-style: italic;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const LegalMention = styled.p`
  width: 100%;
  padding: 9px;
  border-radius: 8px;
  background-color: var(--color-primary-light);
  text-align: center;
  margin-top: 16px;
`;

type SelectOption = {
  key: string;
  label: string;
};

export const putAutreAtTheEnd = (a: SelectOption, b: SelectOption) => {
  if (a.key.includes('Autre')) return 1;
  if (b.key.includes('Autre')) return -1;
  return 0;
};

export const alphabetically = (a: SelectOption, b: SelectOption) =>
  a.label.localeCompare(b.label);

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Entrez un email valide')
    .required('Champ obligatoire'),
  password: yup
    .string()
    .min(8, 'Votre mot de passe doit contenir au moins 8 caractères')
    .required('Champ obligatoire'),
  firstName: yup.string().required('Champ obligatoire'),
  lastName: yup.string().required('Champ obligatoire'),
  company: yup.string().required('Champ obligatoire'),
  companyType: yup.string().required('Champ obligatoire'),
  tos: yup
    .boolean()
    .oneOf([true], "Vous devez accepter les conditions d'utilisation"),
  country: yup.string().required('Champ obligatoire'),
  region: yup.string().when('country', {
    is: (country: string) =>
      country &&
      countryAndRegions[country as keyof typeof countryAndRegions]?.length > 0,
    then: yup
      .string()
      .required('Champ obligatoire pour les régions de ce pays'),
  }),
});

const Register = () => {
  const { t, i18n } = useTranslation(['authentication', 'common']);
  const {
    loading,
    login,
    error: authError,
    register: registerUser,
  } = useAuthentication();
  const navigate = useNavigate();
  const [showRegion, setShowRegion] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      company: '',
      companyType: '',
      country: '',
      region: '',
      tos: false,
      news: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await registerUser({ ...values, language: i18n.language });
      await login(values.email, values.password);
      navigate('/');
    },
  });

  const studios = Object.keys(companyData);
  const companyTypesValues = [...new Set(Object.values(companyData))];
  const companyTypes = companyTypesValues
    .map((key) => ({
      key,
      label: t(`authentication:register.company-types.${key}`),
    }))
    .sort(alphabetically)
    .sort(putAutreAtTheEnd);

  const countries = Object.keys(countryAndRegions)
    .map((key) => ({
      key,
      label: t(`common:countries.${key}`),
    }))
    .sort(alphabetically)
    .sort(putAutreAtTheEnd);

  const regions = showRegion
    ? Object.values(
        countryAndRegions[
          formik.values.country as keyof typeof countryAndRegions
        ],
      ).map((key) => ({
        key,
        label: t(`common:regions.${key}`),
      }))
    : [];

  useEffect(() => {
    const isRegion =
      formik.values.country.length > 0 &&
      countryAndRegions[formik.values.country as keyof typeof countryAndRegions]
        ?.length > 0;
    setShowRegion(isRegion);
    if (!isRegion) {
      formik.setFieldValue('region', '');
    }
  }, [formik.values.country, countryAndRegions]);

  useEffect(() => {
    const studio = formik.values.company;
    if (studio && studios.includes(studio)) {
      const selectedCompanyType =
        companyData[studio as keyof typeof companyData];
      formik.setFieldValue('companyType', selectedCompanyType);
    }
  }, [formik.values.company, companyData]);

  return (
    <>
      <Container>
        <form onSubmit={formik.handleSubmit}>
          <Block>
            <Header>
              <h2>{t('authentication:register.title')}</h2>
              <LanguageSwitcher />
            </Header>
            {authError instanceof Error && (
              <ErrorBlock accent>{`${authError.message}`}</ErrorBlock>
            )}
            <TwoColumnContainer>
              <TextField
                id="email"
                name="email"
                label={t('authentication:register.mail-address')}
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={loading}
                required
              />
              <TextField
                id="password"
                name="password"
                type="password"
                label={t('authentication:register.password')}
                value={formik.values.password}
                onChange={formik.handleChange}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                disabled={loading}
                required
              />
            </TwoColumnContainer>

            <TwoColumnContainer>
              <TextField
                id="firstName"
                name="firstName"
                label={t('authentication:register.firstname')}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={formik.touched.firstName && formik.errors.firstName}
                disabled={loading}
                required
                fullWidth
              />
              <TextField
                id="lastName"
                name="lastName"
                label={t('authentication:register.lastname')}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={formik.touched.lastName && formik.errors.lastName}
                disabled={loading}
                required
                fullWidth
              />
            </TwoColumnContainer>

            <TwoColumnContainer>
              <Autocomplete
                id="company"
                fullWidth
                freeSolo
                options={studios}
                value={formik.values.company}
                onBlur={(event) => {
                  // onChange not triggered after a value selection followed by its edit and finally blur
                  formik.setFieldValue(
                    'company',
                    (event.target as HTMLInputElement).value || '',
                  );
                }}
                onChange={(event, newValue) => {
                  formik.setFieldValue('company', newValue || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t('authentication:register.company')}*`}
                    error={
                      formik.touched.company && Boolean(formik.errors.company)
                    }
                    helperText={formik.touched.company && formik.errors.company}
                    disabled={loading}
                    fullWidth
                  />
                )}
              />
              <FormControl fullWidth>
                <InputLabel id="companyType-label">
                  {t('authentication:register.company-type')}
                </InputLabel>
                <Select
                  id="companyType"
                  name="companyType"
                  label={`${t('authentication:register.company-type')}*`}
                  value={formik.values.companyType}
                  required
                  onChange={(e) =>
                    formik.setFieldValue(
                      'companyType',
                      e.target.value as string,
                    )
                  }
                  error={
                    formik.touched.companyType &&
                    Boolean(formik.errors.companyType)
                  }
                  disabled={loading}
                >
                  {companyTypes.map(({ key, label }) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TwoColumnContainer>

            <TwoColumnContainer>
              <FormControl fullWidth>
                <InputLabel id="country-label">
                  {`${t('edit-profile-modal.country')}*`}
                </InputLabel>
                <Select
                  id="country"
                  name="country"
                  label={`${t('edit-profile-modal.country')}*`}
                  value={formik.values.country}
                  required
                  onChange={(e) =>
                    formik.setFieldValue('country', e.target.value as string)
                  }
                  error={
                    formik.touched.country && Boolean(formik.errors.country)
                  }
                  disabled={loading}
                >
                  {countries.map(({ key, label }) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {showRegion && (
                <FormControl fullWidth>
                  <InputLabel id="region-label">
                    {`${t('edit-profile-modal.region')}*`}
                  </InputLabel>
                  <Select
                    id="region"
                    name="region"
                    label={`${t('edit-profile-modal.region')}*`}
                    value={formik.values.region}
                    onChange={(e) =>
                      formik.setFieldValue('region', e.target.value as string)
                    }
                    error={
                      formik.touched.region && Boolean(formik.errors.region)
                    }
                    disabled={loading}
                  >
                    {regions
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map(({ key, label }) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </TwoColumnContainer>

            <FormControl
              required
              error={formik.touched.tos && Boolean(formik.errors.tos)}
              component="fieldset"
              variant="standard"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    id="tos"
                    name="tos"
                    checked={formik.values.tos}
                    onChange={(_, checked) =>
                      formik.setFieldValue('tos', checked)
                    }
                  />
                }
                label={
                  <TosContainer>
                    <Markdown>{t('authentication:register.tos')}</Markdown>
                  </TosContainer>
                }
              />
              {formik.touched.tos && (
                <FormHelperText>{formik.errors.tos}</FormHelperText>
              )}
            </FormControl>
            <FormControl
              required
              error={formik.touched.news && Boolean(formik.errors.news)}
              component="fieldset"
              variant="standard"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    id="news"
                    name="news"
                    checked={formik.values.news}
                    onChange={(_, checked) =>
                      formik.setFieldValue('news', checked)
                    }
                  />
                }
                label={t('authentication:register.news')}
              />
            </FormControl>
            <LoadingButton
              type="submit"
              loading={loading}
              color="primary"
              disabled={loading}
              fullWidth
            >
              {t('authentication:register.submit')}
            </LoadingButton>
            <SideActionsContainer>
              <Link to="/authentication/login">
                {t('authentication:login.title')}
              </Link>
            </SideActionsContainer>
            <LegalMention className="hxr">
              {t('authentication:register.legal-mention')}
            </LegalMention>
          </Block>
        </form>
      </Container>
    </>
  );
};

export default Register;
